import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLanguage } from "./Language";
import { ObrPinGeneration, useOBRSettings } from "./OBRSettings";
import axios from "axios";

export interface PINResetFormProps {
  borrowerNumber: number;
  verificationCode: string;
}

export interface ResetPinRequestBody {
  borrowerNumber: number;
  verificationCode: string;
  newPIN: string;
  confirmNewPIN: string;
}

enum ValidationErrorType {
  Length = "Length",
  Required = "Required",
  Mismatch = "Mismatch",
}

export const PINResetForm: React.FC<PINResetFormProps> = (props) => {

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { borrowerNumber, verificationCode } = props;

  const { obrConfiguration } = useOBRSettings();

  if (!borrowerNumber) {
    throw "borrowerNumber missing";
  }

  if (!verificationCode) {
    throw "verificationCode missing";
  }
  const [userVerified, setUserVerified] = React.useState<boolean>(null);
  React.useEffect(() => {
    axios
      .get<boolean>("/account/checkVerificationCode", {
        params: {
          borrowerNumber,
          verificationCode,
        },
      })
      .then(({ data: verified }) => {
        setError(verified !== true);
        setUserVerified(verified);
      })
      .catch(() => {
        setError(true);
        setUserVerified(false);
      });
  }, [borrowerNumber, verificationCode]);

  React.useEffect(() => {
    if (userVerified !== null && obrConfiguration !== null) {
      setLoading(false);
    }
  }, [userVerified, obrConfiguration]);

  const { translate } = useLanguage();

  const pinMinLength = React.useRef(1);
  const pinMaxLength = React.useRef(8);
  const pinDefaultHelperText = React.useRef("");
  const pinLengthError = React.useRef("");
  const pinBlankError = React.useRef("");
  const pinMismatchError = React.useRef("");
  const pinRange = React.useRef("");

  React.useEffect(() => {
    pinBlankError.current = translate("requiredFieldError");
    pinMismatchError.current = translate("pinMismatchMsg");
    if (
      obrConfiguration &&
      obrConfiguration.obrEnabled &&
      obrConfiguration.howToGeneratePin == ObrPinGeneration.None &&
      obrConfiguration.pinLengthRequired !== null
    ) {
      pinMinLength.current = obrConfiguration.pinLengthRequired;
      pinMaxLength.current = obrConfiguration.pinLengthRequired;
      pinRange.current = `${obrConfiguration.pinLengthRequired}`;
    } else {
      pinMinLength.current = 1;
      pinMaxLength.current = 8;
      pinRange.current = `1 - 8`;
    }
    pinDefaultHelperText.current = translate("ci_PINLengthHint", {
      PINLENGTH: pinRange.current,
    });
    pinLengthError.current = translate("pinLengthOutOfRange", {
      PINLENGTH: pinRange.current,
    });

  }, [obrConfiguration, translate]);

  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(true);

  const [error, setError] = React.useState<boolean>(false);
  const [formSubmitted, setFormSubmitted] = React.useState<boolean>(false);
  const [pin, setPIN] = React.useState<string>("");
  const [pinError, setPINError] = React.useState<ValidationErrorType | null>(
    ValidationErrorType.Required
  );
  const [success, setSuccess] = React.useState<boolean>(false);

  const [pinHelperText, setPINHelperText] = React.useState(
    pinDefaultHelperText.current
  );
  const [pinTouched, setPINTouched] = React.useState<boolean>(false);

  const [confirmPIN, setConfirmPIN] = React.useState<string>("");
  const [confirmPINError, setConfirmPINError] =
    React.useState<ValidationErrorType | null>(ValidationErrorType.Required);
  const [confirmPINHelperText, setConfirmPINHelperText] =
    React.useState<string>(" ");
  const [confirmPINTouched, setConfirmPINTouched] =
    React.useState<boolean>(false);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormSubmitted(true);
    updateConfirmPINError(confirmPIN, pin);
    updatePINError(pin);

    if(confirmPINError || pinError) {
      console.log("Form has invalid fields");
      return;
    }

    const postData: ResetPinRequestBody = {
      borrowerNumber,
      verificationCode,
      newPIN: pin,
      confirmNewPIN: confirmPIN,
    };
    axios
      .post<string>("/account/completePINReset", postData)
      .then((response) => {
        if (response.status === 200) {
          setSuccess(true);
          setOpen(false);
        }
      })
      .catch((error) => {
        // frontend validation covers everything, we have to assume the link has expired.
        setUserVerified(false);
        setError(true);
        console.error(error);
      });
  };

  React.useEffect(() => {

    if (pinError && (pinTouched || formSubmitted)) {
      switch (pinError) {
        case ValidationErrorType.Length:
          setPINHelperText(pinLengthError.current);
          break;
        case ValidationErrorType.Required:
          setPINHelperText(pinBlankError.current);
          break;
      }
    } else {
      setPINHelperText(pinDefaultHelperText.current);
    }
  }, [
    pinError,
    pinTouched,
    formSubmitted,
    translate,
    pinDefaultHelperText,
    pinLengthError,
    pinBlankError,
  ]);

  React.useEffect(() => {
    if (confirmPINError && (confirmPINTouched || formSubmitted)) {
      switch (confirmPINError) {
        case ValidationErrorType.Required:
          setConfirmPINHelperText(pinBlankError.current);
          break;
        case ValidationErrorType.Mismatch:
          setConfirmPINHelperText(pinMismatchError.current);
          break;
      }
    } else {
      setConfirmPINHelperText(" ");
    }
  }, [
    confirmPINError,
    confirmPINTouched,
    formSubmitted,
    translate,
    pinMismatchError,
    pinBlankError,
  ]);

  const updatePINError = (pin: string) => {
    const pinLength = pin.trim().length;
    if (pinLength === 0) {
      setPINError(ValidationErrorType.Required);
      return;
    }

    if (pinLength > pinMaxLength.current || pinLength < pinMinLength.current) {
      setPINError(ValidationErrorType.Length);
      return;
    }

    setPINError(null);
  };

  const onChangePIN = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pin = event.target.value;
    setPIN(pin);
    updateConfirmPINError(confirmPIN, pin);
    updatePINError(pin);
  };

  const onBlurPIN = (event: React.FocusEvent<HTMLInputElement>) => {
    setPINTouched(true);
    updatePINError(pin);
  };

  const updateConfirmPINError = (confirmPIN: string, pin: string) => {
    const confirmPINLength = confirmPIN.trim().length;
    if (confirmPINLength === 0) {
      setConfirmPINError(ValidationErrorType.Required);
      return;
    }

    if (confirmPIN !== pin) {
      setConfirmPINError(ValidationErrorType.Mismatch);
      return;
    }

    setConfirmPINError(null);
  };

  const onChangeConfirmPIN = (event: React.ChangeEvent<HTMLInputElement>) => {
    const confirmPIN = event.target.value;
    setConfirmPIN(confirmPIN);
    updateConfirmPINError(confirmPIN, pin);
  };

  const onBlurConfirmPIN = (event: React.FocusEvent<HTMLInputElement>) => {
    setConfirmPINTouched(true);
    updateConfirmPINError(confirmPIN, pin);
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    // if (reason === 'clickaway') {
    //   return;
    // }

    setSuccess(false);
  };

  return (
    <React.Fragment>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={success}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={handleCloseSnackbar}
          sx={{ width: "100%" }}
        >
          {translate("pinChangedMsg")}
        </Alert>
      </Snackbar>
      <Dialog open={open} fullScreen={fullScreen}>
        <DialogContent sx={{width: fullScreen ? "auto" : "320px"}}>
          <DialogContentText textAlign="center">
            {translate("resetPIN")}
          </DialogContentText>
          {!success && !loading && (
            <form onSubmit={handleSubmit} noValidate>
              <Stack
                direction="column"
                sx={{
                  gap: 2,
                  paddingTop: 1
                }}>
                {error && (
                  <Alert severity="error">{translate("expiredLinkMsg")}</Alert>
                )}
                <TextField
                  label={translate("newPINMsg")}
                  disabled={!userVerified}
                  name="pin"
                  type="password"
                  value={pin}
                  error={pinError !== null && (pinTouched || formSubmitted)}
                  helperText={pinHelperText}
                  onChange={onChangePIN}
                  onBlur={onBlurPIN}
                  autoFocus
                  required
                ></TextField>
                <TextField
                  label={translate("confirmNewPINMsg")}
                  disabled={!userVerified}
                  name="confirmPin"
                  type="password"
                  error={
                    confirmPINError !== null &&
                    (confirmPINTouched || formSubmitted)
                  }
                  value={confirmPIN}
                  helperText={confirmPINHelperText}
                  onChange={onChangeConfirmPIN}
                  onBlur={onBlurConfirmPIN}
                  required
                ></TextField>
                <Button type="submit" disabled={!userVerified} variant="contained" color="primary">
                  {translate("resetPIN")}
                </Button>
                <Button
                  type="button"
                  variant="text"
                  onClick={() => setOpen(false)}
                >
                  {translate("cancel")}
                </Button>
              </Stack>
            </form>
          )}
          {loading && (
            <Stack
              direction="column"
              sx={{
                gap: 2,
                paddingTop: 1
              }}>
              <Skeleton>
                <TextField helperText=" "></TextField>
              </Skeleton>
              <Skeleton>
                <TextField helperText=" "></TextField>
              </Skeleton>
              <Skeleton height={"36.5px"} width={"100%"}></Skeleton>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};
